import { Spacing } from '@persona-id/design-tokens';
import { ChevronDownIcon, ChevronUpIcon } from '@persona-id/design-tokens/icons';
import { produce } from 'immer';
import { isEmpty } from 'lodash';
import { capitalize, get, keyBy, sortBy } from 'lodash/fp';
import { MutableRefObject, RefObject } from 'react';
import styled from 'styled-components';

import DiffIcon from 'dashboard/apps/InquiryTemplateEditor/DiffIcon';
import DiffLabel from 'dashboard/apps/InquiryTemplateEditor/DiffLabel';
import { REQUIRE_SELFIE_MATCH_OPTIONS } from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionSelfieMatch/FormSectionSelfieMatch';
import { VerificationTemplateVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { EMPTY_LABEL } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffSummary/InquiryTemplateVersionDiffSummary';
import {
  Column,
  EnabledIdClassesByCountry,
  IdClassDiffArrayDiff,
  IdClassesByCountryDiff,
  ObjectArrayDiff,
  ValueDiff,
  VerificationTemplateVersionDiff,
  VersionDiff,
  VersionDiffChangeType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { valueDiffIsNotEmpty } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/utils';
import Table, {
  Cell,
  PropertyRow,
  Row,
  TableRowWithDiffTag,
} from 'dashboard/apps/InquiryTemplateEditor/VersionDiffTable';
import { TableRowWithDiffTagProps } from 'dashboard/apps/InquiryTemplateEditor/VersionDiffTable/TableRowWithDiffTag';
import { REQUIRES_SIDES_OPTIONS } from 'dashboard/apps/VerificationTemplateEditor/forms/FormSectionCountryConfigurations/FormSectionCountryConfigurations';
import { isGovIdVtmplVersion } from 'dashboard/forms/FormVerificationTemplateChecks/BarcodeCheckModal';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import {
  CountryCodeLower,
  GovernmentIdEnabledIdClassByCountries,
  GovernmentIdEnabledIdClassByCountryEntry,
  RequiredDevice,
  SelfieMatch,
  VerificationTemplateVersion,
  VerificationTemplateVersionEmailAddress,
  VerificationTemplateVersionGovernmentId,
  VerificationTemplateVersionPhoneNumber,
  VerificationTemplateVersionSelfie,
} from 'dashboard/models/verification-template-version';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import {
  GOVERNMENT_ID_CLASSES,
  GOVERNMENT_ID_DISPLAY_STRINGS,
  GovernmentIdClass,
} from 'lib/constants/governmentIds';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';
import { VerificationStatus } from 'lib/verification';
import { CheckName } from 'lib/verificationCheck';

function isSelfieVtmplVersion(
  vtmplVersion: VerificationTemplateVersion | undefined,
): vtmplVersion is VerificationTemplateVersionSelfie {
  switch (vtmplVersion?.type) {
    case ApiModel.VerificationTemplateVersionSelfie:
      return true;
    default:
      return false;
  }
}

function isPhoneNumberVtmplVersion(
  vtmplVersion: VerificationTemplateVersion | undefined,
): vtmplVersion is VerificationTemplateVersionPhoneNumber {
  switch (vtmplVersion?.type) {
    case ApiModel.VerificationTemplateVersionPhoneNumber:
      return true;
    default:
      return false;
  }
}

function isEmailAddressVtmplVersion(
  vtmplVersion: VerificationTemplateVersion | undefined,
): vtmplVersion is VerificationTemplateVersionEmailAddress {
  switch (vtmplVersion?.type) {
    case ApiModel.VerificationTemplateVersionEmailAddress:
      return true;
    default:
      return false;
  }
}

const safelyAccessCheckDisplayName = (checkName: CheckName) => {
  if (checkName in CHECK_DISPLAY_CONFIG) {
    return CHECK_DISPLAY_CONFIG[checkName as CheckName].name;
  } else {
    return checkName
      .replace(/-/g, ' ')
      .split(' ')
      .map((s) => capitalize(s))
      .join(' ');
  }
};

const StyledLabel = styled(Label)`
  text-decoration: underline;
`;

const ClassIdDisplayContainer = styled.div`
  margin: ${Spacing[3]} 0;
`;

const IdClassContainer = styled.div`
  min-height: ${Spacing[9]};
  margin: ${Spacing[2]} 0;
`;

const IdClassTitleContainer = styled.div<{ displayDiffIcon: boolean }>`
  alignitems: center;
  display: flex;
  gap: ${Spacing[1]};
  ${({ displayDiffIcon }) => {
    if (displayDiffIcon) return 'margin-left: -20px;';
  }}
`;

const generateCheckStatus = (check: VerificationCheckConfig) => {
  switch (check['toggle-option']) {
    case 'enabled': {
      const AndRequired = check['on-failed'] === VerificationStatus.Failed ? ' & Required' : '';
      return `Enabled${AndRequired}`;
    }
    default:
      return 'Disabled';
  }
};

// This display function doesn't utilize the check diff since there is only one property.
// The overall change type tag is sufficient to determine how the check was modified.
function CheckDisplay({ check }: { check: VerificationCheckConfig }) {
  return <PropertyRow textKey='Status' value={generateCheckStatus(check)} />;
}

function EnabledChecksVersionDiffTable({
  enabledCheckChanges,
  verificationChecks,
  showUnchanged,
}: {
  enabledCheckChanges: ObjectArrayDiff<VerificationCheckConfig> | undefined;
  verificationChecks: VerificationCheckConfig[];
  showUnchanged: boolean;
}) {
  const [isOpen, { toggle }] = useToggle(true);

  if (isEmpty(enabledCheckChanges) && !showUnchanged) {
    return <></>;
  }

  return (
    <>
      <Row headerRow onClick={toggle} id='version_diff__enabled_checks_row'>
        <Cell>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isOpen ? <ChevronDownIcon /> : <ChevronUpIcon />}
            <Spacer width={0.5} height={0} />
            <Label weight='bold'>Checks</Label>
          </div>
        </Cell>
        <Cell></Cell>
      </Row>
      {isOpen && enabledCheckChanges && (
        <>
          {Object.entries(enabledCheckChanges).map(([checkName, checkDiff], index) => {
            if (checkDiff['change-type'] === VersionDiffChangeType.MODIFIED) {
              return (
                <TableRowWithDiffTag
                  key={index}
                  label={safelyAccessCheckDisplayName(checkName as CheckName)}
                  changeType={checkDiff?.['change-type']}
                  baseNode={<CheckDisplay check={checkDiff.meta['base-value']} />}
                  targetNode={<CheckDisplay check={checkDiff.meta['target-value']} />}
                />
              );
            }
          })}
          {showUnchanged &&
            verificationChecks
              .filter((verificationCheck) => !(verificationCheck.check in enabledCheckChanges))
              .map((verificationCheck, index) => {
                return (
                  <TableRowWithDiffTag
                    key={index}
                    label={safelyAccessCheckDisplayName(verificationCheck.check)}
                    changeType={undefined}
                    baseNode={<CheckDisplay check={verificationCheck} />}
                    targetNode={<CheckDisplay check={verificationCheck} />}
                  />
                );
              })}
        </>
      )}
    </>
  );
}

const generateEmptyClassId = (
  idClass: GovernmentIdClass,
): GovernmentIdEnabledIdClassByCountryEntry => ({
  class: idClass,
  'requires-sides': [],
  'accepted-classes': [],
  'requires-device': RequiredDevice.None,
});

// When rendering the list of ID classes for a specific inquiry template verison, we need to
// add the removed or added class ids (compared to the other version) to maintain the alignment
// between id classes when displaying them side by side
const mergeRemovedOrAddedClassIds = (
  classIds: GovernmentIdEnabledIdClassByCountryEntry[],
  classIdDiff: IdClassDiffArrayDiff,
  changeColumn: Column,
) => {
  return produce(classIds, (draft) => {
    Object.entries(classIdDiff).forEach(([idClass, idClassDiff]) => {
      switch (changeColumn) {
        case Column.BASE:
          if (idClassDiff['change-type'] === VersionDiffChangeType.ADDED)
            draft.push(generateEmptyClassId(idClass as GovernmentIdClass));
          break;
        case Column.TARGET:
          if (idClassDiff['change-type'] === VersionDiffChangeType.REMOVED)
            draft.push(generateEmptyClassId(idClass as GovernmentIdClass));
          break;
      }
    });
  });
};

interface ClassIdDisplayProps {
  classId: EnabledIdClassesByCountry;
  classIdDiff?: IdClassDiffArrayDiff;
  changeColumn?: Column;
  showUnchanged?: boolean;
}

/*
 * This function is used to display individual ID classes for a specific country
 * @param classId - The object containing the enabled ID classes for a specific country
 * @param classIdDiff - The diff object for the enabled ID classes
 * @param changeColumn - The column where this component is being displayed
 */
function ClassIdDisplay({
  classId,
  classIdDiff,
  changeColumn,
  showUnchanged,
}: ClassIdDisplayProps) {
  const changeType =
    changeColumn === Column.BASE ? VersionDiffChangeType.REMOVED : VersionDiffChangeType.ADDED;

  let classIds = classId['id-classes'];
  if (changeColumn != null && classIdDiff != null) {
    // Ensure class IDs are aligned when displayed side by side
    classIds = mergeRemovedOrAddedClassIds(classId['id-classes'], classIdDiff, changeColumn);
  }

  return (
    <>
      {sortBy((classId) => classId['class'], classIds).map((idClass, index) => {
        const {
          class: governmentIdClass,
          'requires-sides': requiresSides,
          'accepted-classes': acceptedClasses,
        } = idClass;

        if (governmentIdClass == null || (!get(governmentIdClass, classIdDiff) && !showUnchanged))
          return;

        // If both requiresSides and acceptedClasses are empty, we don't need to display anything
        const requiresSidesAndAcceptedClassesEmpty =
          requiresSides.length === 0 && acceptedClasses.length === 0;

        // If we're in the target column, we only care about the values that were added
        // If we're in the base column, we only care about the values that were removed
        const addedOrRemoved = changeColumn === Column.TARGET ? 'added' : 'removed';
        const requiresSidesDiff =
          get([governmentIdClass, 'changes', 'requires-sides', addedOrRemoved], classIdDiff) || [];
        const acceptedClassesDiff =
          get([governmentIdClass, 'changes', 'accepted-classes', addedOrRemoved], classIdDiff) ||
          [];
        const governmentIdClassChangeType = get([governmentIdClass, 'change-type'], classIdDiff);
        const displayDiffIcon =
          governmentIdClassChangeType === changeType ||
          governmentIdClassChangeType === VersionDiffChangeType.MODIFIED;

        return (
          <ClassIdDisplayContainer key={index}>
            <IdClassTitleContainer displayDiffIcon={displayDiffIcon}>
              {displayDiffIcon && <DiffIcon changeType={governmentIdClassChangeType} />}
              <StyledLabel weight='bold'>
                {GOVERNMENT_ID_DISPLAY_STRINGS[governmentIdClass]}
              </StyledLabel>
            </IdClassTitleContainer>
            {requiresSidesAndAcceptedClassesEmpty ? (
              <IdClassContainer>{EMPTY_LABEL}</IdClassContainer>
            ) : (
              <IdClassContainer>
                <PropertyRow
                  textKey='Required Sides'
                  value={REQUIRES_SIDES_OPTIONS.filter((side) =>
                    requiresSides.includes(side.value),
                  ).map((side, sideIndex) => (
                    <>
                      {sideIndex > 0 && ', '}
                      <DiffLabel
                        key={sideIndex}
                        changeType={requiresSidesDiff.includes(side.value) ? changeType : null}
                      >
                        {side.label}
                      </DiffLabel>
                    </>
                  ))}
                />
                <PropertyRow
                  textKey='Accepted ID Classes'
                  value={acceptedClasses.map((acceptedClass, acceptedClassIndex) => (
                    <>
                      {acceptedClassIndex > 0 && ', '}
                      <DiffLabel
                        key={acceptedClassIndex}
                        changeType={
                          acceptedClassesDiff.includes(acceptedClass as string) ? changeType : null
                        }
                      >
                        {
                          GOVERNMENT_ID_DISPLAY_STRINGS[
                            acceptedClass as (typeof GOVERNMENT_ID_CLASSES)[number]
                          ]
                        }
                      </DiffLabel>
                    </>
                  ))}
                />
              </IdClassContainer>
            )}
          </ClassIdDisplayContainer>
        );
      })}
    </>
  );
}

/* This component is used to display the enabled countries and ID types for government ID verifications
 * @param idClassByCountryChange - The diff object for the enabled countries and ID types
 * @param idClassByCountry - The object containing the enabled countries and
 *                           ID types (only used when showUnchanged is true)
 * @param showUnchanged - A boolean to determine if we should display unchanged values
 */
function IdClassByCountryVersionDiffTable({
  idClassByCountryChange,
  idClassByCountry,
  showUnchanged,
}: {
  idClassByCountryChange: IdClassesByCountryDiff | null | undefined;
  idClassByCountry: GovernmentIdEnabledIdClassByCountries;
  showUnchanged: boolean;
}) {
  const [isOpen, { toggle }] = useToggle(true);

  if (isEmpty(idClassByCountryChange) && !showUnchanged) {
    return <></>;
  }

  return (
    <>
      <Row headerRow onClick={toggle} id='version_diff__id_class_by_country_row'>
        <Cell>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isOpen ? <ChevronDownIcon /> : <ChevronUpIcon />}
            <Spacer width={0.5} height={0} />
            <Label weight='bold'>Enabled countries and ID types</Label>
          </div>
        </Cell>
        <Cell></Cell>
      </Row>
      {isOpen && (
        <>
          {idClassByCountryChange && (
            <>
              {Object.entries(idClassByCountryChange).map(([country, countryDiff], index) => {
                const tableRowWithDiffTagProps: TableRowWithDiffTagProps = {
                  baseNode: EMPTY_LABEL,
                  targetNode: EMPTY_LABEL,
                  label: COUNTRIES[country.toUpperCase() as CountryCode],
                  changeType: countryDiff['change-type'],
                };
                switch (countryDiff['change-type']) {
                  case VersionDiffChangeType.MODIFIED:
                    tableRowWithDiffTagProps.baseNode = (
                      <ClassIdDisplay
                        classId={countryDiff.meta['base-value']}
                        classIdDiff={countryDiff.changes['id-classes']}
                        changeColumn={Column.BASE}
                        showUnchanged={showUnchanged}
                      />
                    );
                    tableRowWithDiffTagProps.targetNode = (
                      <ClassIdDisplay
                        classId={countryDiff.meta['target-value']}
                        classIdDiff={countryDiff.changes['id-classes']}
                        changeColumn={Column.TARGET}
                        showUnchanged={showUnchanged}
                      />
                    );
                    break;
                  case VersionDiffChangeType.ADDED:
                    tableRowWithDiffTagProps.targetNode = (
                      <ClassIdDisplay
                        classId={countryDiff.meta['value']}
                        changeColumn={Column.TARGET}
                        showUnchanged={true}
                      />
                    );
                    break;
                  case VersionDiffChangeType.REMOVED:
                    tableRowWithDiffTagProps.baseNode = (
                      <ClassIdDisplay
                        classId={countryDiff.meta['value']}
                        changeColumn={Column.BASE}
                        showUnchanged={true}
                      />
                    );
                    break;
                  default:
                    assertNever(countryDiff['change-type']);
                }

                return <TableRowWithDiffTag key={index} {...tableRowWithDiffTagProps} />;
              })}
            </>
          )}

          {showUnchanged &&
            Object.entries(idClassByCountry).map(([countryCode, idClasses], index) => {
              if (idClassByCountryChange != null && countryCode in idClassByCountryChange) return;
              const classId = {
                country: countryCode as CountryCodeLower,
                'id-classes': idClasses,
              };
              return (
                <TableRowWithDiffTag
                  key={index}
                  label={COUNTRIES[countryCode.toUpperCase() as CountryCode]}
                  changeType={undefined}
                  baseNode={
                    <ClassIdDisplay
                      classId={classId}
                      changeColumn={Column.BASE}
                      showUnchanged={showUnchanged}
                    />
                  }
                  targetNode={
                    <ClassIdDisplay
                      classId={classId}
                      changeColumn={Column.TARGET}
                      showUnchanged={showUnchanged}
                    />
                  }
                />
              );
            })}
        </>
      )}
    </>
  );
}

function ValueDiffTable<T>({
  changes,
  currentValue,
  showUnchanged,
  textKey,
  transformValue = (value: T) => String(value),
}: {
  changes: ValueDiff<T> | null;
  currentValue: T;
  showUnchanged: boolean;
  textKey: string;
  transformValue?: (value: T) => string;
}) {
  const hasChanges = valueDiffIsNotEmpty(changes);
  const changeType = hasChanges ? VersionDiffChangeType.MODIFIED : null;

  if (hasChanges || showUnchanged) {
    return (
      <Row>
        <Cell>
          <PropertyRow
            textKey={textKey}
            changeType={changeType}
            value={transformValue(hasChanges ? changes.removed : currentValue)}
          />
        </Cell>
        <Cell>
          <PropertyRow
            textKey={textKey}
            value={transformValue(hasChanges ? changes.added : currentValue)}
            changeType={changeType}
          />
        </Cell>
      </Row>
    );
  }
  return <></>;
}

interface VerificationDiffTableProps {
  verificationTemplateVersion: VerificationTemplateVersion;
  verificationTemplateVersionChanges: VerificationTemplateVersionDiff | null;
  showUnchanged: boolean;
}

const VerificationDiffTable = ({
  verificationTemplateVersion,
  verificationTemplateVersionChanges,
  showUnchanged,
}: VerificationDiffTableProps) => {
  let additionalRows;

  switch (true) {
    case isGovIdVtmplVersion(verificationTemplateVersion):
      additionalRows = (
        <IdClassByCountryVersionDiffTable
          idClassByCountryChange={
            verificationTemplateVersionChanges?.changes?.['enabled-id-classes-by-country']
          }
          idClassByCountry={
            (verificationTemplateVersion as VerificationTemplateVersionGovernmentId)?.attributes[
              'enabled-id-classes-by-country'
            ]
          }
          showUnchanged={showUnchanged}
        />
      );
      break;
    case isSelfieVtmplVersion(verificationTemplateVersion): {
      const selfieMatchLabelMap = keyBy('value', REQUIRE_SELFIE_MATCH_OPTIONS);
      additionalRows = (
        <ValueDiffTable<SelfieMatch>
          changes={verificationTemplateVersionChanges?.changes?.['selfie-match'] || null}
          currentValue={
            (verificationTemplateVersion as VerificationTemplateVersionSelfie)?.attributes?.[
              'require-selfie-match'
            ]
          }
          showUnchanged={showUnchanged}
          textKey='Selfie Poses'
          transformValue={(value: SelfieMatch) => {
            return selfieMatchLabelMap[value].label;
          }}
        />
      );
      break;
    }
    case isPhoneNumberVtmplVersion(verificationTemplateVersion):
    case isEmailAddressVtmplVersion(verificationTemplateVersion):
      additionalRows = (
        <ValueDiffTable<number>
          changes={verificationTemplateVersionChanges?.changes?.['num-attempts'] || null}
          currentValue={
            (verificationTemplateVersion as VerificationTemplateVersionPhoneNumber)?.attributes?.[
              'num-attempts'
            ]
          }
          showUnchanged={showUnchanged}
          textKey='Num Attempts'
        />
      );
      break;

    default:
      additionalRows = null;
  }

  return (
    <>
      {additionalRows}
      <EnabledChecksVersionDiffTable
        enabledCheckChanges={verificationTemplateVersionChanges?.changes?.['verification-checks']}
        verificationChecks={verificationTemplateVersion?.attributes?.['verification-checks']}
        showUnchanged={showUnchanged}
      />
    </>
  );
};
export const VerificationTemplateVersionDiffTable = ({
  verificationTemplateVersion,
  verificationTemplateVersionChanges,
}: {
  verificationTemplateVersion: VerificationTemplateVersion;
  verificationTemplateVersionChanges: VerificationTemplateVersionDiff | null;
}) => {
  const [showUnchanged, { toggle: toggleShowUnchanged }] = useToggle(false);

  const noChanges =
    verificationTemplateVersionChanges == null ||
    (isEmpty(verificationTemplateVersionChanges.changes['verification-checks']) &&
      isEmpty(verificationTemplateVersionChanges.changes['enabled-id-classes-by-country']) &&
      isEmpty(verificationTemplateVersionChanges.changes['selfie-match']?.added) &&
      isEmpty(verificationTemplateVersionChanges.changes['selfie-match']?.removed) &&
      verificationTemplateVersionChanges.changes['num-attempts']?.added == null &&
      verificationTemplateVersionChanges.changes['num-attempts']?.removed == null);

  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={[
            <ToggleSlider
              key='show-unchanged-toggle'
              titleOn='displaying all values'
              titleOff='only showing changes'
              labelOn='Show unchanged'
              labelOff='Show unchanged'
              value={showUnchanged}
              onChange={toggleShowUnchanged}
            />,
          ]}
        >
          {verificationTemplateVersion?.attributes?.name} Verification
        </Card.Title>

        {noChanges && !showUnchanged ? (
          <Label color='secondary' size='lg'>
            No changes made
          </Label>
        ) : (
          <Table>
            <Row>
              <Cell>
                <Label>Previous Version</Label>
              </Cell>
              <Cell>
                <Label>Current Version</Label>
              </Cell>
            </Row>
            <VerificationDiffTable
              verificationTemplateVersion={verificationTemplateVersion}
              verificationTemplateVersionChanges={verificationTemplateVersionChanges}
              showUnchanged={showUnchanged}
            />
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

interface InquiryTemplateVersionDiffVerificationProps {
  versionDiff: VersionDiff;
  verificationTemplateVersionMap: VerificationTemplateVersionMap;
  verificationTemplateVersionIdToRefMap: MutableRefObject<
    Record<string, RefObject<HTMLDivElement>>
  >;
}

const InquiryTemplateVersionDiffVerification = ({
  versionDiff,
  verificationTemplateVersionMap,
  verificationTemplateVersionIdToRefMap,
}: InquiryTemplateVersionDiffVerificationProps) => {
  const { 'verification-template-versions': verificationTemplateVersionDiff } =
    versionDiff.inquiryTemplateVersionDiff;
  return (
    <>
      {Object.entries(verificationTemplateVersionMap).map(
        ([verificationTemplateVersionId, verificationTemplateVersion], index) => {
          // We use verification template tokens as the identifier in the verification template version
          // diff object since verification template version tokens are different between inquiry template versions
          const verificationTemplateToken =
            verificationTemplateVersion?.relationships?.['verification-template']?.data?.id;
          return (
            <>
              <div
                key={index}
                ref={
                  (verificationTemplateVersionIdToRefMap.current[verificationTemplateVersionId] ??=
                    {
                      current: null,
                    })
                }
              >
                <VerificationTemplateVersionDiffTable
                  verificationTemplateVersion={
                    verificationTemplateVersion as VerificationTemplateVersion
                  }
                  verificationTemplateVersionChanges={
                    verificationTemplateToken
                      ? verificationTemplateVersionDiff[verificationTemplateToken]
                      : null
                  }
                />
              </div>
              <Spacer height={5} />
            </>
          );
        },
      )}
    </>
  );
};

export default InquiryTemplateVersionDiffVerification;
